import { useState, useEffect, useCallback } from 'react';
import { Share2, Trash2 } from 'lucide-react';
import { useI18n } from '@/context/I18nContext';
import { listTeamShares, unshareSession, type Share } from '@/api/client';
import { useRouter } from '@/context/RouterContext';
import { useAuth } from '@/context/AuthContext';
import { Pagination } from '@/components/common/Pagination';
import { FilterBar } from '@shared/search';
import { teamSharingSchema } from './teamSharingSchema';
import { TeamShareWizardDialog } from './TeamShareWizardDialog';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';

interface Props {
  teamId: string;
  canManage: boolean;
}

export function TeamSharingTab({ teamId, canManage }: Props) {
  const { t } = useI18n();
  const { params, navigate } = useRouter();
  const { user } = useAuth();
  
  const hashParams = new URLSearchParams(window.location.hash.split('?')[1] || '');
  const initialQuery = hashParams.get('q') || '';
  const initialPage = parseInt(hashParams.get('page') || '1', 10);

  const [page, setPage] = useState(initialPage);
  const [query, setQuery] = useState(initialQuery);

  const [shares, setShares] = useState<Share[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [sharesLoading, setSharesLoading] = useState(false);
  const [showShareWizard, setShowShareWizard] = useState(false);

  useEffect(() => {
    const handleHashChange = () => {
      const p = new URLSearchParams(window.location.hash.split('?')[1] || '');
      setQuery(p.get('q') || '');
      setPage(parseInt(p.get('page') || '1', 10));
    };
    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, []);

  const loadShares = useCallback(() => {
    setSharesLoading(true);
    listTeamShares(teamId, page, 10, query)
      .then(res => {
        setShares(res.shares || []);
        setTotalCount(res.totalCount || 0);
      })
      .catch(console.error)
      .finally(() => setSharesLoading(false));
  }, [teamId, page, query]);

  useEffect(() => { loadShares(); }, [loadShares]);

  const updateUrl = useCallback((q: string, p: number) => {
    const base = `#/teams/${teamId}/sharing`;
    const searchParams = new URLSearchParams();
    if (q) searchParams.set('q', q);
    if (p > 1) searchParams.set('page', String(p));
    const qs = searchParams.toString();
    window.history.replaceState(null, '', qs ? `${base}?${qs}` : base);
    setQuery(q);
    setPage(p);
  }, [teamId]);

  const handleSearch = useCallback((newQuery: string) => {
    updateUrl(newQuery, 1);
  }, [updateUrl]);

  const handlePageChange = useCallback((newPage: number) => {
    updateUrl(query, newPage);
  }, [updateUrl, query]);

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h3 className="section-title">{t('sharing.title')}</h3>
        <button
          onClick={() => setShowShareWizard(true)}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium bg-accent text-white hover:bg-accent-hover transition-colors cursor-pointer"
        >
          <Share2 size={14} />
          {t('sharing.shareWithTeam')}
        </button>
      </div>

      <div className="mb-4">
        <FilterBar
          schema={teamSharingSchema}
          initialQuery={query}
          onSearch={handleSearch}
          enableFilterUI={true}
        />
      </div>

      <div className="card overflow-hidden">
        {sharesLoading ? (
          <div className="py-8 text-center text-text-muted text-sm"><LoadingSpinner /></div>
        ) : shares.length === 0 ? (
          <div className="py-8 text-center text-text-muted text-sm">
            {t('sharing.noSharesYet')}
          </div>
        ) : (
          <div className="divide-y divide-border-subtle">
            {shares.map(share => (
              <div key={share.id} className="px-4 py-3 flex items-center justify-between gap-3 hover:bg-bg-hover transition-colors">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-text-primary truncate">
                      {share.owner?.name || share.ownerId}
                    </span>
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[0.65rem] font-medium border ${
                      share.shareAll
                        ? 'bg-green/15 text-green border-green/30'
                        : 'bg-accent/15 text-accent border-accent/30'
                    }`}>
                      {share.shareAll ? t('sharing.sharedAll') : t('sharing.sharedSelected')}
                    </span>
                  </div>
                  <div className="text-xs text-text-muted mt-0.5">
                    {!share.shareAll && share.sessionIds?.length > 0 && (
                      <span className="mono">{t('sharing.selectedSessions').replace('{count}', String(share.sessionIds.length))}</span>
                    )}
                    {share.createdAt && (
                      <span className="ml-2 mono">{new Date(share.createdAt).toLocaleDateString()}</span>
                    )}
                  </div>
                </div>
                {(canManage || share.ownerId === user?.id) && (
                  <button
                    onClick={async () => {
                      try {
                        await unshareSession(share.id);
                        loadShares();
                      } catch (e) {
                        console.error(e);
                      }
                    }}
                    className="p-1.5 text-text-muted hover:text-red transition-colors cursor-pointer shrink-0"
                    title={t('sharing.revoke')}
                  >
                    <Trash2 size={14} />
                  </button>
                )}
              </div>
            ))}
          </div>
        )}

        {!sharesLoading && totalCount > 10 && (
          <div className="p-4 flex justify-center">
            <Pagination
              page={page}
              totalPages={Math.ceil(totalCount / 10)}
              onPageChange={handlePageChange}
            />
          </div>
        )}
      </div>

      <TeamShareWizardDialog
        open={showShareWizard}
        onClose={() => setShowShareWizard(false)}
        onSuccess={() => { setShowShareWizard(false); loadShares(); }}
        teamId={teamId}
      />
    </div>
  );
}
